import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './NetworkInjection.css';

const NetworkInjection = () => {
  const { user, isInJail, getJailTimeRemaining } = useAuth();
  const [networkStatus, setNetworkStatus] = useState({
    activeAttacks: [],
    incomingAttacks: [],
    networkInjectorTools: 0,
    networkInjectionParts: 0,
    malwareParts: 0,
    firewallParts: 0
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(new Set());
  const [vaultLootingProgress, setVaultLootingProgress] = useState({});

  useEffect(() => {
    fetchNetworkStatus();
    fetchVaultLootingProgress();
    // Auto-refresh every 5 seconds for real-time updates
    const interval = setInterval(() => {
      fetchNetworkStatus();
      fetchVaultLootingProgress();
    }, 5000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchNetworkStatus = async () => {
    try {
      if (!user?.token) return;

      const response = await api.get('/treasure-hunt/network-attacks/status');
      const data = response.data;
      
      if (data.success) {
          setNetworkStatus({
            activeAttacks: data.data.activeAttacks || [],
            incomingAttacks: data.data.incomingAttacks || [],
            networkInjectorTools: data.data.networkInjectorTools || 0,
            networkInjectionParts: data.data.networkInjectionParts || 0,
            malwareParts: data.data.malwareParts || 0,
            firewallParts: data.data.firewallParts || 0
          });
          
          // Show notifications for completed attacks
          if (data.data.completedAttacks && data.data.completedAttacks.length > 0) {
            data.data.completedAttacks.forEach(attack => {
              if (attack.notification) {
                alert(`${attack.notification.title}\n\n${attack.notification.message}`);
              }
            });
          }
          
          setError(null);
        } else {
          setError(data.message || 'Failed to load network status');
        }
    } catch (err) {
      console.error('Error fetching network status:', err);
      setError('Network error occurred');
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchVaultLootingProgress = async () => {
    try {
      if (!user?.token) return;

      const response = await api.get('/treasure-hunt/casinos/vault/progress');
      const data = response.data;
      
      if (data.success && data.data) {
        setVaultLootingProgress(data.data);
      } else {
        setVaultLootingProgress({});
      }
    } catch (err) {
      console.error('Error fetching vault looting progress:', err);
      setVaultLootingProgress({});
    }
  };

  const cancelNetworkAttack = async (targetUserId, targetUsername, attackType) => {
    const isCasinoAttack = attackType === 'casino_vault';
    const isMalwareAttack = attackType === 'casino_malware';
    const attackTypeText = isCasinoAttack ? 'casino vault attack' : 
                          isMalwareAttack ? 'malware attack' : 
                          'network injection attack';
    
    if (!window.confirm(`Are you sure you want to cancel the ${attackTypeText} on ${targetUsername}?`)) {
      return;
    }

    try {
      setCancelling(prev => new Set(prev).add(targetUserId));

      let endpoint, requestBody;
      if (isCasinoAttack) {
        endpoint = '/api/treasure-hunt/casinos/vault/stop-attack';
        requestBody = { casinoId: targetUserId };
      } else if (isMalwareAttack) {
        endpoint = '/api/malware/stop';
        // Find the attack in the current state to get the malwareAttackId
        const currentAttack = networkStatus.activeAttacks.find(attack => attack.targetUserId === targetUserId);
        requestBody = { attackId: currentAttack?.malwareAttackId || targetUserId };
      } else {
        endpoint = '/api/treasure-hunt/network-attacks/cancel';
        requestBody = { targetUserId };
      }

      const response = await api.post(endpoint.replace('/api', ''), requestBody);
      const data = response.data;

      if (data.success) {
        // Remove the cancelled attack from state immediately
        setNetworkStatus(prev => ({
          ...prev,
          activeAttacks: prev.activeAttacks.filter(attack => attack.targetUserId !== targetUserId)
        }));
        
        // Show success message
        alert(`✅ ${isCasinoAttack ? 'Casino vault attack' : isMalwareAttack ? 'Malware attack' : 'Network injection attack'} on ${targetUsername} has been cancelled.`);
      } else {
        alert(`❌ Failed to cancel attack: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error cancelling attack:', err);
      alert('❌ Network error occurred while cancelling attack');
    } finally {
      setCancelling(prev => {
        const newSet = new Set(prev);
        newSet.delete(targetUserId);
        return newSet;
      });
    }
  };

  const startStage3Looting = async (casinoId, casinoName) => {
    if (!window.confirm(`🎰 Start Stage 3: Vault Looting at ${casinoName}?\n\n💰 Earn 3 WR coins per second\n🚨 SWAT will arrive randomly - be ready to escape!\n⏰ Duration: 30 minutes to 14 hours`)) {
      return;
    }

    try {
      const response = await api.post('/treasure-hunt/casinos/vault/loot', { casinoId });
      const data = response.data;

      if (data.success) {
        alert(`🎯 Stage 3: Vault Looting Started!\n\n${data.message}\n\nEarning 3 WR coins per second!\nSWAT arrival: ${data.data.swatArrivalTime || 'Random timing'}`);
        fetchNetworkStatus(); // Refresh the status
      } else {
        alert(`❌ Failed to start vault looting: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error starting vault looting:', err);
      alert('❌ Network error occurred while starting vault looting');
    }
  };

  const stopVaultLooting = async () => {
    if (!window.confirm(`🛑 Stop vault looting?\n\nYou will keep all coins earned so far but stop earning more.\nSWAT timer will be cancelled.`)) {
      return;
    }

    try {
      const response = await api.post('/treasure-hunt/casinos/vault/stop', {});
      const data = response.data;

      if (data.success) {
        alert(`✅ Vault looting stopped!\n\n${data.message}`);
        fetchNetworkStatus(); // Refresh the status
        fetchVaultLootingProgress(); // Refresh looting status
      } else {
        alert(`❌ Failed to stop vault looting: ${data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error stopping vault looting:', err);
      alert('❌ Network error occurred while stopping vault looting');
    }
  };

  const formatTimeRemaining = (estimatedCompletion) => {
    const now = new Date();
    const end = new Date(estimatedCompletion);
    const diffMs = end - now;
    
    if (diffMs <= 0) return 'Completing...';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getProgressPercentage = (startTime, estimatedCompletion) => {
    const start = new Date(startTime);
    const end = new Date(estimatedCompletion);
    const now = new Date();
    
    const totalDuration = end - start;
    const elapsed = now - start;
    
    const percentage = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    return percentage;
  };


  // Only show loading on first visit, not on refreshes
  if (initialLoading) {
    return (
      <div className="network-injection">
        <div className="loading">Loading network injection status...</div>
      </div>
    );
  }

  return (
    <div className="network-injection">
      <div className="network-header">
        <h2>🌐 Network Injection Control Center</h2>
        <p>Monitor and manage your active network injection attacks and casino vault operations</p>
      </div>

      {/* Tools Overview */}
      <div className="tools-overview">
        <div className="tool-stat">
          <span className="tool-icon">⚙️</span>
          <div className="tool-info">
            <div className="tool-count">{networkStatus.networkInjectorTools}</div>
            <div className="tool-label">Network Injector Tools</div>
            <div className="tool-desc">Each tool reduces attack time by 1 hour</div>
          </div>
        </div>
        <div className="tool-stat">
          <span className="tool-icon">🛡️</span>
          <div className="tool-info">
            <div className="tool-count">{networkStatus.firewallParts}</div>
            <div className="tool-label">Firewall Parts</div>
            <div className="tool-desc">Each part reduces incoming damage by 1%</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Jail Status Warning */}
      {isInJail() && (
        <div className="jail-warning" style={{
          background: '#ff4444',
          color: 'white',
          padding: '15px',
          margin: '10px 0',
          borderRadius: '8px',
          border: '2px solid #ff6666'
        }}>
          <h3>🔒 You are currently in jail!</h3>
          <p>Time remaining: {getJailTimeRemaining()} minutes</p>
          <p>You cannot start new network injection attacks while incarcerated.</p>
        </div>
      )}

      {/* Casino Vault Hack - Stage 1 (Network Injection) */}
      {user.casinoHack && user.casinoHack.isActive && user.casinoHack.currentStage === 1 && (
        <div className="casino-stage1" style={{
          background: 'linear-gradient(135deg, #3498db, #2980b9)',
          border: '2px solid #5dade2',
          borderRadius: '12px',
          padding: '20px',
          margin: '20px 0',
          color: 'white'
        }}>
          <div className="stage1-header" style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: '0', fontSize: '24px', color: '#fff' }}>
              🌐 Stage 1: Network Injection - {user.casinoHack.casinoName}
            </h3>
            <p style={{ margin: '10px 0 0 0', color: '#ecf0f1', fontSize: '16px' }}>
              Infiltrating casino security networks...
            </p>
          </div>
          

          <div className="stage1-info" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div className="info-item" style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>🛡️</div>
              <div style={{ fontSize: '12px', color: '#bdc3c7' }}>SECURITY LEVEL</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>High</div>
            </div>
            <div className="info-item" style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>⚡</div>
              <div style={{ fontSize: '12px', color: '#bdc3c7' }}>SWAT RISK</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>10-30%</div>
            </div>
            <div className="info-item" style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '15px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>🔧</div>
              <div style={{ fontSize: '12px', color: '#bdc3c7' }}>NETWORK TOOLS</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{networkStatus.networkInjectorTools}</div>
            </div>
          </div>

          <div className="stage1-status" style={{
            background: 'rgba(0,0,0,0.2)',
            padding: '15px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0', fontSize: '14px', color: '#ecf0f1' }}>
              🎯 <strong>Objective:</strong> Breach casino firewall and discover employee email addresses for Stage 2
            </p>
          </div>
        </div>
      )}

      {/* Active Attacks (Outgoing) */}
      <div className="attacks-section">
        <h3>🎯 Active Network Injection Attacks ({networkStatus.activeAttacks.length})</h3>
        
        {networkStatus.activeAttacks.length === 0 ? (
          <div className="no-attacks">
            <div className="no-attacks-content">
              <span className="no-attacks-icon">💤</span>
              <h4>No Active Network Attacks</h4>
              <p>You currently have no network injection attacks in progress.</p>
              <p>Visit the Hack The Planet map to target other players' computers.</p>
            </div>
          </div>
        ) : (
          <div className="attacks-grid">
            {networkStatus.activeAttacks.map((attack, index) => {
              const timeRemaining = formatTimeRemaining(attack.estimatedCompletion);
              const progress = getProgressPercentage(attack.startTime, attack.estimatedCompletion);
              const isCompleting = timeRemaining === 'Completing...';
              const isCancelling = cancelling.has(attack.targetUserId);
              
              return (
                <div key={`${attack.targetUserId}-${index}`} className={`attack-card ${isCompleting ? 'completing' : ''}`}>
                  <div className="attack-header">
                    <div className="target-info">
                      <span className="target-icon">
                        {attack.attackType === 'casino_vault' ? '🎰' : 
                         attack.attackType === 'casino_malware' ? '🦠' : '👤'}
                      </span>
                      <div className="target-details">
                        <h4>{attack.targetUsername}</h4>
                        <div className="attack-type">
                          {attack.attackType === 'casino_vault' ? 'Casino Vault Attack (Network Injection Stage)' : 
                           attack.attackType === 'casino_malware' ? 'Casino Malware Attack (Stage 2)' : 
                           'Network Injection Attack'}
                        </div>
                      </div>
                    </div>
                    <div className="attack-status">
                      {isCompleting ? (
                        <span className="status-completing">🔄 Completing</span>
                      ) : (
                        <span className="status-active">⚡ Active</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="progress-section">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">
                      {progress.toFixed(1)}% Complete
                    </div>
                  </div>
                  
                  <div className="attack-details">
                    <div className="detail-row">
                      <span className="detail-label">⏰ Time Remaining:</span>
                      <span className="detail-value">{timeRemaining}</span>
                    </div>
                    {attack.attackType === 'casino_vault' && attack.stage1TimeRemaining !== null && (
                      <div className="detail-row">
                        <span className="detail-label">🎯 Stage 1 Remaining:</span>
                        <span className="detail-value">{attack.stage1TimeRemaining}h</span>
                      </div>
                    )}
                    {attack.attackType === 'casino_malware' && attack.stage2TimeRemaining !== null && (
                      <div className="detail-row">
                        <span className="detail-label">🦠 Stage 2 Remaining:</span>
                        <span className="detail-value">{attack.stage2TimeRemaining}h</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="detail-label">🕐 Started:</span>
                      <span className="detail-value">{new Date(attack.startTime).toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">
                        {attack.attackType === 'casino_malware' ? '🦠 Malware Parts Owned:' : '⚙️ Network Injection Parts Owned:'}
                      </span>
                      <span className="detail-value">
                        {attack.attackType === 'casino_malware' ? 
                          (networkStatus.malwareParts || 0) : 
                          (networkStatus.networkInjectionParts || 0)
                        }
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">📏 Duration:</span>
                      <span className="detail-value">{attack.originalDuration?.toFixed(1) || 'Unknown'} hours</span>
                    </div>
                    {attack.attackType === 'casino_malware' && attack.targetEmail && (
                      <div className="detail-row">
                        <span className="detail-label">📧 Target Email:</span>
                        <span className="detail-value" style={{fontSize: '12px', wordBreak: 'break-all'}}>{attack.targetEmail}</span>
                      </div>
                    )}
                    {attack.attackType === 'casino_malware' && attack.swatChance && (
                      <div className="detail-row">
                        <span className="detail-label">🚨 SWAT Risk:</span>
                        <span className="detail-value">{Math.round(attack.swatChance * 100)}%</span>
                      </div>
                    )}
                    {attack.potentialGain > 0 && (
                      <div className="detail-row potential-gain">
                        <span className="detail-label">💰 Potential Gain:</span>
                        <span className="detail-value">{attack.potentialGain.toLocaleString()} WR Coins</span>
                      </div>
                    )}
                    
                    {/* Show Stage 1 completion message without email for completed Stage 1 casino attacks */}
                    {attack.attackType === 'casino_vault' && attack.stage1Complete && (
                      <div className="stage1-complete">
                        <div className="stage1-header">
                          <span className="stage1-icon">✅</span>
                          <strong>Stage 1 Complete - Network Infiltration Successful!</strong>
                        </div>
                        <div className="stage1-instructions">
                          <p style={{ color: '#00ff88', marginBottom: '10px' }}>
                            Security systems compromised at {new Date(attack.estimatedCompletion).toLocaleString()}
                          </p>
                          <p style={{ color: '#ffff00', marginBottom: '10px' }}>
                            🎯 <strong>Next Step:</strong> Go to System → Tools → Malware Tool to discover employee email and start Stage 2
                          </p>
                          <small style={{ color: '#ff8888' }}>
                            💡 <strong>Tip:</strong> The malware tool will automatically discover the employee email when you access it
                          </small>
                        </div>
                      </div>
                    )}
                    
                    {/* Show stage 2 completion and stage 3 activation for completed stage 2 casino attacks */}
                    {attack.attackType === 'casino_vault' && attack.stage2Complete && (
                      <div className="stage2-complete">
                        <div className="stage2-header">
                          <span className="stage2-icon">🦠</span>
                          <strong>Stage 2 Complete - Malware Infiltration Successful!</strong>
                        </div>
                        <div className="stage2-details">
                          <p style={{ color: '#00ff88', marginBottom: '10px' }}>
                            Employee system compromised at {new Date(attack.stage2CompletionTime).toLocaleString()}
                          </p>
                          <p style={{ color: '#ffff00', marginBottom: '15px' }}>
                            🎯 <strong>Ready for Stage 3:</strong> Vault looting at 3 WR coins/second with SWAT risk
                          </p>
                          <button
                            onClick={() => startStage3Looting(attack.targetUserId, attack.targetUsername)}
                            style={{
                              width: '100%',
                              padding: '12px',
                              border: '2px solid #ff4500',
                              borderRadius: '6px',
                              backgroundColor: '#ff4500',
                              color: '#fff',
                              fontFamily: 'monospace',
                              fontSize: '16px',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              marginBottom: '10px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#ff6500';
                              e.target.style.borderColor = '#ff6500';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#ff4500';
                              e.target.style.borderColor = '#ff4500';
                            }}
                          >
                            💰 START STAGE 3: VAULT LOOTING 💰
                          </button>
                          <small style={{ color: '#ff8888' }}>
                            ⚠️ <strong>Warning:</strong> SWAT will arrive randomly during looting. Be ready to escape!
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="attack-actions">
                    <button 
                      className="cancel-button"
                      onClick={() => cancelNetworkAttack(attack.targetUserId, attack.targetUsername, attack.attackType)}
                      disabled={isCompleting || isCancelling}
                    >
                      {isCancelling ? 'Cancelling...' : '🚫 Cancel Attack'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Incoming Attacks */}
      {networkStatus.incomingAttacks.length > 0 && (
        <div className="attacks-section">
          <h3>🔴 Incoming Network Attacks ({networkStatus.incomingAttacks.length})</h3>
          
          <div className="attacks-grid">
            {networkStatus.incomingAttacks.map((attack, index) => {
              const timeRemaining = formatTimeRemaining(attack.estimatedCompletion);
              const progress = getProgressPercentage(attack.startTime, attack.estimatedCompletion);
              const isCompleting = timeRemaining === 'Completing...';
              
              return (
                <div key={`${attack.attackerId}-${index}`} className={`attack-card incoming ${isCompleting ? 'completing' : ''}`}>
                  <div className="attack-header">
                    <div className="target-info">
                      <span className="target-icon">⚠️</span>
                      <div className="target-details">
                        <h4>{attack.attackerUsername}</h4>
                        <div className="attack-type">Attacking Your Computer</div>
                      </div>
                    </div>
                    <div className="attack-status">
                      {isCompleting ? (
                        <span className="status-completing">🔄 Finalizing</span>
                      ) : (
                        <span className="status-incoming">🔴 Incoming</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="progress-section">
                    <div className="progress-bar incoming">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="progress-text">
                      {progress.toFixed(1)}% Complete
                    </div>
                  </div>
                  
                  <div className="attack-details">
                    <div className="detail-row">
                      <span className="detail-label">⏰ Time Remaining:</span>
                      <span className="detail-value">{timeRemaining}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">🕐 Started:</span>
                      <span className="detail-value">{new Date(attack.startTime).toLocaleString()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">🛡️ Your Defense:</span>
                      <span className="detail-value">{attack.defenseLevel || 0} firewall parts (-{attack.defenseLevel || 0}%)</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">📏 Duration:</span>
                      <span className="detail-value">{attack.originalDuration?.toFixed(1) || 'Unknown'} hours</span>
                    </div>
                    {attack.potentialLoss > 0 && (
                      <div className="detail-row potential-loss">
                        <span className="detail-label">⚠️ Potential Loss:</span>
                        <span className="detail-value">{attack.potentialLoss.toLocaleString()} WR Coins</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="defense-info">
                    <div className="defense-note">
                      🛡️ Your {networkStatus.firewallParts} firewall parts will reduce stolen amount by {networkStatus.firewallParts}% (minimum 1% can be stolen)
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Stage 3 Vault Looting */}
      {vaultLootingProgress?.isLooting && (
        <div className="attacks-section">
          <h3>💰 Active Vault Looting (Stage 3)</h3>
          
          <div className="looting-card" style={{
            border: '2px solid #ff4500',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: 'rgba(255, 69, 0, 0.1)',
            marginBottom: '20px'
          }}>
            <div className="looting-header" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <div>
                <h4 style={{ color: '#ff4500', margin: 0 }}>🎰 Casino Vault Looting</h4>
                <div style={{ color: '#00ff41', fontSize: '14px' }}>
                  Stage 3: Earning 3 WR coins per second
                </div>
              </div>
              <div className="looting-status" style={{
                padding: '8px 12px',
                backgroundColor: '#ff4500',
                color: '#fff',
                borderRadius: '4px',
                fontWeight: 'bold'
              }}>
                💰 ACTIVE
              </div>
            </div>
            
            {/* Real-time coin counter */}
            <div className="coin-counter" style={{
              textAlign: 'center',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#000',
              border: '2px solid #00ff41',
              borderRadius: '6px'
            }}>
              <div style={{ fontSize: '24px', color: '#00ff41', fontWeight: 'bold', marginBottom: '5px' }}>
                💰 {Math.floor(vaultLootingProgress.currentLooted || 0).toLocaleString()} WR Coins
              </div>
              <div style={{ fontSize: '14px', color: '#ffff00' }}>
                +{vaultLootingProgress.ratePerSecond || 3} coins/second • {Math.floor(vaultLootingProgress.secondsLooted || 0)} seconds looted
              </div>
            </div>
            
            {/* SWAT countdown timer */}
            <div className="swat-timer" style={{
              marginBottom: '15px'
            }}>
              <div style={{ 
                color: vaultLootingProgress.isSwatWarningTime ? '#ff0000' : '#ffff00', 
                fontSize: '16px', 
                fontWeight: 'bold',
                marginBottom: '10px',
                textAlign: 'center'
              }}>
                🚨 SWAT Arrival: {vaultLootingProgress.timeUntilSwat > 0 ? 
                  `${Math.floor(vaultLootingProgress.timeUntilSwat / (1000 * 60 * 60))}h ${Math.floor((vaultLootingProgress.timeUntilSwat % (1000 * 60 * 60)) / (1000 * 60))}m` : 
                  'SWAT HAS ARRIVED!'
                }
              </div>
              
              {/* SWAT progress bar */}
              <div className="swat-progress-bar" style={{
                width: '100%',
                height: '12px',
                backgroundColor: '#333',
                borderRadius: '6px',
                overflow: 'hidden',
                marginBottom: '10px'
              }}>
                <div 
                  className="swat-progress-fill" 
                  style={{
                    height: '100%',
                    backgroundColor: vaultLootingProgress.isSwatWarningTime ? '#ff0000' : 
                                   vaultLootingProgress.timeUntilSwat < (30 * 60 * 1000) ? '#ff8800' : '#00ff41',
                    width: `${Math.max(0, Math.min(100, 100 - (vaultLootingProgress.timeUntilSwat / (vaultLootingProgress.maxDuration * 1000) * 100)))}%`,
                    transition: 'all 0.3s ease'
                  }}
                ></div>
              </div>
              
              {vaultLootingProgress.isSwatWarningTime && (
                <div style={{ 
                  color: '#ff0000', 
                  fontSize: '14px', 
                  textAlign: 'center',
                  backgroundColor: 'rgba(255, 0, 0, 0.2)',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ff0000'
                }}>
                  ⚠️ <strong>SWAT WARNING:</strong> Move your computer 500+ miles to escape!
                </div>
              )}
            </div>
            
            {/* Stop looting button */}
            <div className="looting-actions" style={{ textAlign: 'center' }}>
              <button
                onClick={() => stopVaultLooting()}
                style={{
                  padding: '10px 20px',
                  border: '2px solid #ff0000',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: '#ff0000',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#ff0000';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#ff0000';
                }}
              >
                🛑 Stop Vault Looting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="network-info">
        <h3>ℹ️ Network Injection Information</h3>
        <div className="info-content">
          <div className="info-item">
            <span className="info-icon">🎯</span>
            <div className="info-text">
              <strong>Attack Duration:</strong> 12-16 hours base time, reduced by network injector tools (1 hour each, minimum 1 hour total)
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">💰</span>
            <div className="info-text">
              <strong>Rewards:</strong> Steal 5-15% of target's WR Coins (100-10,000 coins) and earn 25 XP
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🛡️</span>
            <div className="info-text">
              <strong>Defense:</strong> Each firewall part reduces stolen percentage by 1% (minimum 1% can be stolen)
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🚫</span>
            <div className="info-text">
              <strong>Movement & Defense:</strong> Moving your computer >5km away will stop attacks. Only way to stop incoming attacks is to move away and report the attacker to SWAT.
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">📊</span>
            <div className="info-text">
              <strong>Attack Limit:</strong> You can only attack one user at a time. Cancel current attack or wait for completion before starting a new one.
            </div>
          </div>
          <div className="info-item">
            <span className="info-icon">🎰</span>
            <div className="info-text">
              <strong>Casino Vault Attacks:</strong> Multi-stage attacks starting with network injection (12-16 hours), followed by malware deployment
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NetworkInjection;